<template src="./template.html"></template>

<script>
import BreadCrumbs from '@/components/BreadCrumbs/index.vue'
import SingleCheckbox from '@/components/Form/Checkbox/SingleCheckbox'
import LoadingSpinner from '@/components/LoadingSpinner/index.vue'
import DriverNewForm from '@/components/RouteTemplates/Form.vue'
import SectionHeader from '@/components/SectionHeader'
import BinitySlider from '@/components/Slider.vue'
import CompanyNav from '@/components/company_nav'
import stringUtils from '@/components/mixins/stringUtils'

import companyUsersApi from '@/api/companyUsers'
import locationsApi from '@/api/locations'
import projectsApi from '@/api/projects'
import routeTemplatesApi from '@/api/routeTemplates'
import vehiclesApi from '@/api/vehicles'

export default {
  name: 'route_template',
  mixins: [stringUtils],
  inject: ['companyUser', 'showPopup'],
  data() {
    return {
      collectionModes: {
        containerLevel: 'container_level',
        pointLevel: 'collection_point_level',
      },
      companyUsers: [],
      deleteOption: false,
      drivers: [],
      includeStaticContainers: false,
      includeContainerGroupAtStop: false,
      loading: true,
      locations: [],
      isEvaluated: false,
      noTemplateTypeError: false,
      presets: [
        {
          value: 1,
          name: this.$t('routes.templatePresetEcoFriendly'),
          values: {
            fair_distribution: 0,
            locality_clustering: 0,
            profitability: 9,
            short_paths: 9,
            worker_reduction: 0,
          },
        },
        {
          value: 2,
          name: this.$t('routes.templatePresetWorkerOriented'),
          values: {
            fair_distribution: 9,
            locality_clustering: 0,
            profitability: 4,
            short_paths: 5,
            worker_reduction: 0,
          },
        },
        {
          value: 3,
          name: this.$t('routes.templatePresetCustomerOriented'),
          values: {
            fair_distribution: 0,
            locality_clustering: 0,
            profitability: 2,
            short_paths: 2,
            worker_reduction: 0,
          },
        },

        {
          value: 4,
          name: this.$t('routes.templatePresetFleetOriented'),
          values: {
            fair_distribution: 0,
            locality_clustering: 0,
            profitability: 2,
            short_paths: 2,
            worker_reduction: 9,
          },
        },
      ],
      projects: [],
      routePreset: 5,
      routeTemplate: {},
      selectedStartPoint: null,
      selectedEndPoint: null,
      selectedCollectionMode: null,
      templateTypes: {
        static: 'static',
        adiutabyte: 'adiutabyte_dynamic',
      },
      templateMode: 'edit',
      vehicles: [],
    }
  },
  computed: {
    formattedAssumedFillLevelStatic: {
      get() {
        return this.includeStaticContainers && this.routeTemplate.assumed_fill_level_static
          ? Math.round(this.routeTemplate.assumed_fill_level_static * 100)
          : 0
      },
      set(newValue) {
        this.routeTemplate.assumed_fill_level_static = newValue / 100
      },
    },
    formattedMinCollectFillLevel: {
      get() {
        return this.routeTemplate.minimal_collect_fill_level
          ? Math.round(this.routeTemplate.minimal_collect_fill_level * 100)
          : 0
      },
      set(newValue) {
        this.routeTemplate.minimal_collect_fill_level = newValue / 100
      },
    },
    locationOptions() {
      return this.locations.map(location => ({
        location: {
          name: location.name,
          address: location.preformatted,
        },
        coordinates: {
          lat: location.lat,
          lng: location.lng,
        },
      }))
    },
  },
  components: {
    BinitySlider,
    BreadCrumbs,
    CompanyNav,
    DriverNewForm,
    LoadingSpinner,
    SectionHeader,
    SingleCheckbox,
  },
  methods: {
    async getData() {
      this.selectedStartPoint = null
      this.selectedEndPoint = null
      ;[this.vehicles, this.projects, this.routeTemplate, this.locations, this.companyUsers] = await Promise.all([
        vehiclesApi.selectableVehicles(this.companyUser.company_uuid),
        projectsApi.getAll(this.companyUser.company_uuid),
        routeTemplatesApi.get(this.$route.params.template_uuid),
        locationsApi.getAllForCompany(this.companyUser.company_uuid),
        companyUsersApi.getWithPagination({ company_uuid: this.companyUser.company_uuid }),
      ])

      this.drivers = this.getUiDriversList(this.routeTemplate)
      this.routeTemplate.vehicles = this.routeTemplate.vehicles.map(vehicleLink => {
        return vehicleLink.vehicle?.uuid
      })

      this.deleteOption = true

      this.selectedStartPoint = {
        lat: this.routeTemplate.start_lat,
        lng: this.routeTemplate.start_lng,
      }

      this.includeContainerGroupAtStop = this.routeTemplate.collection_mode == this.collectionModes.pointLevel

      this.selectedEndPoint = {
        lat: this.routeTemplate.end_lat,
        lng: this.routeTemplate.end_lng,
      }

      this.selectedCollectionMode = this.routeTemplate.collection_mode || this.collectionModes.containerLevel
      this.includeStaticContainers = this.routeTemplate.assumed_fill_level_static > 0
      this.loading = false
    },
    findUserByUuid(uuid) {
      const driverUser = this.companyUsers.find(user => user.user.uuid === uuid)
      return driverUser ? driverUser.user : null
    },
    findVehicleByUuid(uuid) {
      const driverVehicle = this.vehicles.find(vehicle => vehicle.value === uuid)
      return driverVehicle ? driverVehicle : null
    },
    getUiDriversList(routeTemplate) {
      return routeTemplate.drivers.map(driver => {
        return {
          user: this.findUserByUuid(driver.user_uuid),
          vehicle: this.findVehicleByUuid(driver.vehicle_uuid),
        }
      })
    },
    setCustomPreset() {
      this.routePreset = 5
    },
    setPreset(event) {
      var selectedPreset = this.presets.find(x => x.value == event.target.value)
      this.routeTemplate = {
        ...this.routeTemplate,
        ...selectedPreset.values,
      }
    },
    setStartPoint() {
      this.routeTemplate.start_lat = this.selectedStartPoint?.lat
      this.routeTemplate.start_lng = this.selectedStartPoint?.lng
    },
    setEndPoint() {
      this.routeTemplate.end_lat = this.selectedEndPoint?.lat
      this.routeTemplate.end_lng = this.selectedEndPoint?.lng
    },
    setCollectionMode() {
      this.routeTemplate.collection_mode = this.selectedCollectionMode
    },
    async removeTemplate() {
      const popupResult = await this.showPopup({
        type: 'confirm',
        content: this.$t('routes.templateDeleteConfirmation'),
      })
      if (!popupResult) return null

      const response = await routeTemplatesApi.delete(this.routeTemplate.uuid)
      if (response.success) {
        await this.$router.push(`/company/routes`)
      }
    },
    async evaluateTemplate() {
      // Only for Dynamic templates
      if (this.routeTemplate.template_type === this.templateTypes.static) {
        return true
      }

      // Evaluate route template
      const result = await routeTemplatesApi.evaluate({
        company_uuid: this.routeTemplate.company.uuid,
        project_uuid: this.routeTemplate.project_uuid,
        vehicles: this.drivers.map(driver => driver.vehicle.value),
      })
      if (result.fraction_count == 0) {
        const fractionAlert = await this.showPopup({
          content: this.$t('routes.fractionZeroAlert'),
          type: 'confirm',
        })
        if (!fractionAlert) {
          return false
        }
      }

      if (result.matching_container_count == 0) {
        const containerCountAlert = await this.showPopup({
          content: this.$t('routes.containerZeroAlert'),
          type: 'confirm',
        })
        if (!containerCountAlert) {
          return false
        }
      }
      return true
    },
    async onFormSubmitted() {
      this.noTemplateTypeError = !this.routeTemplate.template_type
      if (this.noTemplateTypeError) return

      const evaluateResponse = await this.evaluateTemplate()
      if (!evaluateResponse) return

      this.routeTemplate.vehicles = []
      this.routeTemplate.users = []
      this.drivers.map(driver => {
        this.routeTemplate.vehicles.push(driver.vehicle.value)
        this.routeTemplate.users.push(driver.user.uuid)
      })

      this.routeTemplate.collection_mode = this.includeContainerGroupAtStop
        ? this.collectionModes.pointLevel
        : this.collectionModes.containerLevel

      // update route template
      const response = await routeTemplatesApi.update(this.routeTemplate.uuid, this.routeTemplate)

      if (response.success) {
        await this.getData()
      }
    },
    setTemplateTypeDefaults() {
      if (this.routeTemplate.template_type === this.templateTypes.static) {
        this.routeTemplate.fair_distribution = null
        this.routeTemplate.locality_clustering = null
        this.routeTemplate.profitability = null
        this.routeTemplate.short_paths = null
        this.routeTemplate.worker_reduction = null

        this.routeTemplate.minimal_collect_fill_level = null
      } else if (this.routeTemplate.template_type === this.templateTypes.adiutabyte) {
        this.routeTemplate.fair_distribution = 0
        this.routeTemplate.locality_clustering = 0
        this.routeTemplate.profitability = 0
        this.routeTemplate.short_paths = 0
        this.routeTemplate.worker_reduction = 0

        this.routeTemplate.minimal_collect_fill_level = 0.5
      }
    },
  },
  watch: {
    includeStaticContainers(val) {
      // When checkbox is toggled, we update the assumed fill level static value.
      // The default value for assumed fill level static is 0.7, i.e. 70%.
      // Resetting the value to 0 when it the checkbox is unchecked tells the backend that
      // the static containers should not be included in the route.
      if (val) {
        if (this.routeTemplate.assumed_fill_level_static == 0) this.routeTemplate.assumed_fill_level_static = 0.7
      } else {
        this.routeTemplate.assumed_fill_level_static = 0
      }
    },
    selectedStartPoint() {
      this.setStartPoint()
    },
    selectedEndPoint() {
      this.setEndPoint()
    },
    selectedCollectionMode() {
      this.setCollectionMode()
    },
    routeTemplate: {
      async handler(newValue) {
        if (newValue.template_type) this.noTemplateTypeError = false
      },
      deep: true,
    },
  },
  mounted() {
    this.getData()
  },
}
</script>

<style lang="scss" src="./style.scss" scoped />
